import * as THREE from 'three';

import { Axial, axialKey, axialToWorld } from '../map/HexCoordinates';
import { HexCellData } from '../map/HexSurface';
import { Props } from './PropModels';
import { treesOn } from './PropField';

/**
 * Drzewa, które ktoś właśnie wycina: drgają przy każdym uderzeniu, a na końcu
 * przewracają się i znikają.
 *
 * **Poza partią rekwizytów, bo tylko tak mogą się ruszyć.** Las chunka to jedna
 * scalona partia instancji; pojedynczego drzewa nie da się w niej pochylić.
 * Pole wycinane wypada więc z partii (`ChunkStreamer.felling`), a jego drzewa
 * stają tutaj jako zwykłe siatki — **te same modele w tych samych miejscach**,
 * bo liczy je ta sama funkcja (`treesOn`). Głazy z tego pola zostają w partii.
 *
 * **Rytm uderzeń jest po tej stronie.** Serwer mówi, kiedy wycinka się zaczęła
 * i ile potrwa; kiedy pada topór, nie ma znaczenia dla żadnej reguły, więc nie
 * jedzie po drucie. Dźwięk pada razem z drgnięciem, przez `onChop`.
 */

/** Co ile sekund pada topór. */
const CHOP_EVERY = 1.5;
/** Pierwsze uderzenie tuż po starcie, a nie po pełnym odstępie. */
const FIRST_CHOP = 0.3;
/** O ile najwyżej drzewo odchyla się przy uderzeniu, w radianach. */
const SHAKE_ANGLE = 0.06;
/** Jak szybko drgnięcie gaśnie i jak szybko drga. */
const SHAKE_DECAY = 0.12;
const SHAKE_SPEED = 38;
/** Ile trwa przewracanie się i ile zapadanie po nim. */
const FALL_TIME = 0.9;
const SINK_TIME = 0.7;
/**
 * Ile czekać po terminie na słowo od serwera, zanim drzewo samo wróci do lasu.
 * Zapas na wypadek zgubionego zdarzenia — bez niego drzewo drgałoby wiecznie.
 */
const OVERDUE = 10;

interface Cut {
  cell: Axial;
  /** Jeden obrotnik na drzewo, zaczepiony u jego podstawy. */
  pivots: THREE.Group[];
  holder: THREE.Group;
  /** Kierunek przewrócenia, na płaszczyźnie XZ. */
  toward: THREE.Vector3;
  /** Oś, wokół której drzewo się pochyla: prostopadła do kierunku. */
  axis: THREE.Vector3;
  /** Sekundy od startu. */
  age: number;
  /** Kiedy (w `age`) pada następne uderzenie. */
  nextChop: number;
  /** Ile sekund temu padło ostatnie, `Infinity` przed pierwszym. */
  sinceChop: number;
  /** Kiedy (w `age`) wycinka powinna się skończyć. */
  due: number;
  /** Sekundy od początku upadku, `undefined` dopóki drzewo stoi. */
  falling?: number;
}

export class FellingTrees {
  readonly object = new THREE.Group();
  private readonly cuts = new Map<number, Cut>();

  constructor(
    private readonly world: Map<number, HexCellData>,
    private readonly props: Props,
    private readonly broadleafBiomes: ReadonlySet<number>,
    /** Topór padł na tym polu — tu gra się dźwięk. */
    private readonly onChop: (cell: Axial) => void,
    /** Drzewo wraca do partii: pole trzeba przerysować. */
    private readonly onRelease: (cell: Axial) => void
  ) {
    this.object.name = 'felling';
  }

  /** Czy drzewa tego pola są teraz tutaj, a nie w partii. */
  has(cell: Axial): boolean {
    return this.cuts.has(axialKey(cell.q, cell.r));
  }

  /**
   * Ktoś zaczął rąbać. Odpowiada, czy drzewa zostały wyjęte z partii — wtedy
   * wołający przerysowuje pole.
   */
  start(cell: Axial, from: Axial, seconds: number): boolean {
    const key = axialKey(cell.q, cell.r);
    const known = this.cuts.get(key);
    if (known) {
      // Ten sam las, nowy wycinający albo ponowny start: termin od nowa.
      known.due = known.age + seconds;
      return false;
    }

    const data = this.world.get(key);
    if (!data?.forest) return false;
    const trees = treesOn(data, this.props, this.broadleafBiomes);
    if (trees.length === 0) return false;

    const here = axialToWorld(cell.q, cell.r);
    const there = axialToWorld(from.q, from.r);
    const toward = new THREE.Vector3(here.x - there.x, 0, here.z - there.z);
    if (toward.lengthSq() < 1e-6) {
      // Rąbie z tego samego pola: kierunek z pozycji, żeby był ten sam u wszystkich.
      const angle = ((cell.q * 73856093) ^ (cell.r * 19349663)) % 628;
      toward.set(Math.cos(angle / 100), 0, Math.sin(angle / 100));
    }
    toward.normalize();
    // `up × toward` — dodatni kąt wokół tej osi pochyla wierzchołek ku `toward`.
    const axis = new THREE.Vector3(toward.z, 0, -toward.x);

    const holder = new THREE.Group();
    const position = new THREE.Vector3();
    const rotation = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const pivots = trees.map(({ model, matrix }) => {
      matrix.decompose(position, rotation, scale);
      const pivot = new THREE.Group();
      pivot.position.copy(position);
      const body = new THREE.Group();
      body.quaternion.copy(rotation);
      body.scale.copy(scale);
      for (const part of model.parts) {
        const mesh = new THREE.Mesh(part.geometry, part.material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        body.add(mesh);
      }
      pivot.add(body);
      holder.add(pivot);
      return pivot;
    });
    this.object.add(holder);

    this.cuts.set(key, {
      cell,
      pivots,
      holder,
      toward,
      axis,
      age: 0,
      nextChop: FIRST_CHOP,
      sinceChop: Infinity,
      due: seconds,
    });
    return true;
  }

  /** Wycinka przerwana: drzewa wracają do partii. Odpowiada, czy coś wróciło. */
  stop(cell: Axial): boolean {
    const key = axialKey(cell.q, cell.r);
    const cut = this.cuts.get(key);
    if (!cut || cut.falling !== undefined) return false;
    this.remove(key, cut);
    return true;
  }

  /** Drzewa ścięte: niech się przewrócą. Na polu, którego tu nie ma, nic. */
  fell(cell: Axial): void {
    const cut = this.cuts.get(axialKey(cell.q, cell.r));
    if (cut && cut.falling === undefined) cut.falling = 0;
  }

  update(deltaTime: number): void {
    for (const [key, cut] of this.cuts) {
      cut.age += deltaTime;

      if (cut.falling !== undefined) {
        cut.falling += deltaTime;
        this.fall(cut);
        if (cut.falling >= FALL_TIME + SINK_TIME) this.remove(key, cut, false);
        continue;
      }

      if (cut.age > cut.due + OVERDUE) {
        this.remove(key, cut);
        continue;
      }

      cut.sinceChop += deltaTime;
      if (cut.age >= cut.nextChop && cut.age < cut.due) {
        cut.nextChop += CHOP_EVERY;
        cut.sinceChop = 0;
        this.onChop(cut.cell);
      }

      const t = cut.sinceChop;
      const angle = Number.isFinite(t)
        ? SHAKE_ANGLE * Math.exp(-t / SHAKE_DECAY) * Math.sin(t * SHAKE_SPEED)
        : 0;
      for (const pivot of cut.pivots) pivot.setRotationFromAxisAngle(cut.axis, angle);
    }
  }

  dispose(): void {
    for (const [key, cut] of this.cuts) this.remove(key, cut, false);
    this.object.removeFromParent();
  }

  /** Przewracanie, a po nim zapadanie się w ziemię. */
  private fall(cut: Cut): void {
    const t = cut.falling ?? 0;
    const tip = Math.min(1, t / FALL_TIME);
    // Przyspiesza jak coś, co leci: powoli od pionu, szybko przy ziemi.
    const angle = (Math.PI / 2) * tip * tip;
    const sink = Math.max(0, (t - FALL_TIME) / SINK_TIME);
    for (const pivot of cut.pivots) {
      pivot.setRotationFromAxisAngle(cut.axis, angle);
      pivot.scale.setScalar(1 - sink);
    }
  }

  /**
   * Zdejmij drzewa z tej grupy. `release` mówi, czy mają wrócić do partii —
   * po upadku nie mają, bo pole jest już bez lasu.
   */
  private remove(key: number, cut: Cut, release = true): void {
    this.cuts.delete(key);
    cut.holder.removeFromParent();
    if (release) this.onRelease(cut.cell);
  }
}
